<template>
    <div class="credit-resume-wrapper">
        <div
        v-if="!spinnerActive && creditInfo"
        class="credit-resume">
            <div class="header-section" :class="{ 'mobile': isMobile}">
                <div class="next-payment-section">
                    <div class="label">{{ creditInfo.paidAmountPercentage !== 100 ? t('credits.myCredits.creditResume.nextInstallmentLabel') : t('credits.myCredits.creditResume.payedAmountLabel') }}</div>
                    <div class="amount">{{ amountFormat(creditInfo.paidAmountPercentage !== 100 ? creditInfo.nextInstallmentAmount : creditInfo.paidAmount, currency) }}</div>
                    <div
                    v-if="creditInfo.paidAmountPercentage !== 100"
                    class="day">{{ t('credits.myCredits.creditResume.nextInstallmentDate', { date: getDate(creditInfo.nextInstallmentDate)}) }}</div>
                    <div v-else class="day">{{t('credits.myCredits.creditResume.fullPayedState')}}</div>
                </div>
                <div v-if="!isMobile && creditInfo.paidAmountPercentage !== 100" class="days-left-section">
                    <div class="label">{{ t('credits.myCredits.creditResume.daysLeftLabel') }}</div>
                    <div class="days">{{ t('credits.myCredits.creditResume.daysLeft', { days: creditInfo.daysLeft }) }}</div>
                </div>
            </div>
            <divider-generic :style="'padding-top: 0px'"/>
            <progress-bar :progress="creditInfo.paidAmountPercentage"/>
            <div class="amounts-section" :class="{ 'mobile': isMobile}">
                <div v-if="creditInfo.paidAmountPercentage !== 100" class="payed-amount">
                    <div class="label">{{ t('credits.myCredits.creditResume.payedAmountLabel') }}</div>
                    <div class="amount"><span></span> {{ amountFormat(creditInfo.paidAmount, currency) }}</div>
                </div>
                <div class="amount-to-pay">
                    <div class="label">{{ t('credits.myCredits.creditResume.debtAmountLabel') }}</div>
                    <div class="amount"><span></span> {{ amountFormat(Math.abs(creditInfo.loanLeftToPay), currency) }}</div>
                </div>
            </div>
            <div v-if="isMobile && creditInfo.paidAmountPercentage !== 100" class="days-left-section-mobile">
                <div class="label">{{ t('credits.myCredits.creditResume.daysLeftLabel') }}</div>
                <div class="days">{{ t('credits.myCredits.creditResume.daysLeft', { days: creditInfo.daysLeft }) }}</div>
            </div>
        </div>
        <!-- Spinner -->
        <spinner v-if="spinnerActive"/>
    </div>
</template>

<script setup>
import ProgressBar from '@/components/generic/progressBar/Generic'
import DividerGeneric from '@/components/generic/dividers/Generic'
import Spinner from '@/components/generic/loaders/Generic'

import { amountFormat } from '@/assets/scripts/amounts'
import { getDate } from '@/assets/scripts/dateBuilders'

/* i18n */
import { useI18n } from 'vue-i18n'
const { t } = useI18n()

const props = defineProps({
    creditInfo: Object,
    currency: String,
    isMobile: Boolean,
    spinnerActive: Boolean
})
</script>

<style lang="scss" scoped>
.credit-resume-wrapper {
    width: 100%;
    min-height: 201px;

    display: flex;
    align-items: center;
    justify-content: center;

    background-color: var(--secondary-light-4);
    border-radius: 16px;

    box-shadow: 0px 4px 16px 0px rgba(0, 0, 0, 0.08);

    .credit-resume {
        width: 100%;

        display: flex;
        flex-direction: column;

        padding: 16px;
        gap: 16px;

        .header-section {
            width: 100%;

            display: flex;
            flex-direction: row;
            justify-content: space-between;

            .next-payment-section {
                display: flex;
                flex-direction: column;

                gap: 4px;

                .label {
                    @include H7-Regular;
                    color: var(--secondary-dark-3);
                }

                .amount {
                    @include H3-Regular;
                    color: var(--primary);
                }

                .day {
                    @include H8-Regular;
                    color: var(--secondary-dark-1);
                }
            }

            .days-left-section {
                width: 217px;
                height: 51px;

                display: flex;
                flex-direction: column;
                align-items: center;
                justify-content: space-between;

                padding: 8px 0;
                border-radius: 8px;
                background-color: var(--primary-light-3);

                .label {
                    @include H8-Regular;
                    color: var(--secondary-dark-1);
                }

                .days {
                    @include H6-Medium;
                    color: var(--secondary-dark-2);
                }
            }

            &.mobile {
                .next-payment-section {
                    .label {
                        margin-bottom: 4px;
                    }
                }
            }
        }

        .amounts-section {
            display: flex;
            flex-direction: row;
            gap: 64px;

            .payed-amount {
                display: flex;
                flex-direction: column;
                gap: 8px;

                .label {
                    @include H8-Regular;
                    color: var(--secondary-dark-1);
                }

                .amount {
                    @include H6-Medium;
                    color: var(--secondary-dark-2);

                    position: relative;
                    padding-left: 13px;

                    &::before {
                        content: '';
                        width: 8px;
                        height: 8px;

                        position: absolute;
                        left: 0;
                        top: calc(50% - 4px);

                        border-radius: 50%;
                        background-color: var(--cashflow-entry);
                    }
                }
            }

            .amount-to-pay {
                display: flex;
                flex-direction: column;
                gap: 8px;

                .label {
                    @include H8-Regular;
                    color: var(--secondary-dark-1);
                }

                .amount {
                    @include H6-Medium;
                    color: var(--secondary-dark-2);

                    position: relative;
                    padding-left: 13px;

                    &::before {
                        content: '';
                        width: 8px;
                        height: 8px;

                        position: absolute;
                        left: 0;
                        top: calc(50% - 4px);

                        border-radius: 50%;
                        background-color: var(--secondary-light-1);
                    }
                }
            }

            &.mobile {
                flex-direction: column;
                gap: 8px;
            }
        }

        .days-left-section-mobile {
            width: 100%;
            height: 51px;

            display: flex;
            flex-direction: column;
            align-items: center;
            justify-content: space-between;

            padding: 8px 0;
            border-radius: 8px;
            background-color: var(--primary-light-3);

            .label {
                @include H8-Regular;
                color: var(--secondary-dark-1);
            }

            .days {
                @include H6-Medium;
                color: var(--secondary-dark-2);
            }
        }
    }
}
</style>
